'use client'

import { useState, useEffect } from 'react'
import { User } from '@/types'
import { useUser } from '@/hooks/useUser'
import { useToast } from '@/hooks/useToast'
import { getApiUrl } from '@/lib/apiUrl'


import { ModerationViolation } from '@/lib/contentModeration'

interface ModerationViolationWithUser extends ModerationViolation {
  user: User
  application: {
    id: string
    property: {
      title: string
    }
  }
}

interface ModerationViolationsProps {
  users: User[]
}

export default function ModerationViolations({ }: ModerationViolationsProps) {
  const { token } = useUser()
  const toast = useToast()
  const [violations, setViolations] = useState<ModerationViolationWithUser[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'high' | 'medium' | 'low'>('all')
  const [typeFilter, setTypeFilter] = useState<'all' | 'blocked' | 'warning' | 'suspicious'>('all')

  // Load moderation violations
  const loadViolations = async () => {
    setLoading(true)
    try {
      if (!token) {
        setViolations([])
        return
      }

      const apiUrl = getApiUrl()
      const response = await fetch(`${apiUrl}/admin/moderation-violations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      const payload = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(payload?.message || 'Failed to load moderation violations')
      }

      setViolations(payload?.violations || [])
    } catch (error) {
      console.error('Error:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to load moderation violations')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadViolations()
  }, [])

  // Filter violations
  const filteredViolations = violations.filter(violation => {
    if (filter !== 'all' && violation.severity !== filter) return false
    if (typeFilter !== 'all' && violation.violation_type !== typeFilter) return false
    return true
  })

  // Get severity badge color
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high':
        return 'bg-red-100 text-red-800'
      case 'medium':
        return 'bg-yellow-100 text-yellow-800'
      case 'low':
        return 'bg-blue-100 text-blue-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  // Get violation type badge color
  const getTypeColor = (type: string) => {
    switch (type) {
      case 'blocked':
        return 'bg-red-100 text-red-800'
      case 'warning':
        return 'bg-orange-100 text-orange-800'
      case 'suspicious':
        return 'bg-purple-100 text-purple-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString()
  }

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-2 text-gray-600">Loading moderation violations...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Content Moderation Violations</h2>
          <p className="text-sm text-gray-600">
            Monitor and review flagged messages to maintain platform integrity
          </p>
        </div>
        <button
          onClick={loadViolations}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
        >
          Refresh
        </button>
      </div>

      {/* Alert for high-severity violations */}
      {violations.filter(v => v.severity === 'high').length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                High-Risk Violations Detected
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>
                  <strong>{violations.filter(v => v.severity === 'high').length} high-risk violations</strong> detected. 
                  These require immediate attention as they involve attempts to share contact information or move conversations off-platform.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-medium text-gray-500">Total Violations</h3>
          <p className="text-2xl font-bold text-gray-900">{violations.length}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-medium text-gray-500">High Risk</h3>
          <p className="text-2xl font-bold text-red-600">
            {violations.filter(v => v.severity === 'high').length}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-medium text-gray-500">Blocked Messages</h3>
          <p className="text-2xl font-bold text-red-500">
            {violations.filter(v => v.violation_type === 'blocked').length}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-medium text-gray-500">Today's Violations</h3>
          <p className="text-2xl font-bold text-orange-600">
            {violations.filter(v => {
              const today = new Date().toDateString()
              return new Date(v.created_at!).toDateString() === today
            }).length}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Severity Filter</label>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as 'all' | 'high' | 'medium' | 'low')}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Severities</option>
              <option value="high">High Risk ({violations.filter(v => v.severity === 'high').length})</option>
              <option value="medium">Medium Risk ({violations.filter(v => v.severity === 'medium').length})</option>
              <option value="low">Low Risk ({violations.filter(v => v.severity === 'low').length})</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type Filter</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as 'all' | 'blocked' | 'warning' | 'suspicious')}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Types</option>
              <option value="blocked">Blocked ({violations.filter(v => v.violation_type === 'blocked').length})</option>
              <option value="warning">Warning ({violations.filter(v => v.violation_type === 'warning').length})</option>
              <option value="suspicious">Suspicious ({violations.filter(v => v.violation_type === 'suspicious').length})</option>
            </select>
          </div>
        </div>
      </div>

      {/* Violations List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {filteredViolations.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Property
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Violation
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Message
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredViolations.map((violation) => (
                  <tr key={violation.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {violation.user?.email || 'Unknown User'}
                        </div>
                        <div className="text-sm text-gray-500">
                          ID: {violation.user_id.substring(0, 8)}...
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {violation.application?.property?.title || 'Unknown Property'}
                      </div>
                      <div className="text-sm text-gray-500">
                        App: {violation.application_id.substring(0, 8)}...
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(violation.violation_type)}`}>
                          {violation.violation_type.toUpperCase()}
                        </span>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getSeverityColor(violation.severity)}`}>
                          {violation.severity.toUpperCase()}
                        </span>
                        <div className="text-xs text-gray-600 mt-1">
                          {violation.violation_reason}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="max-w-xs">
                        <div className="text-sm text-gray-900 break-words">
                          <strong>Original:</strong>
                        </div>
                        <div className="text-sm text-gray-700 bg-red-50 p-2 rounded mt-1 break-words">
                          "{violation.original_message}"
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(violation.created_at!)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-6 text-center text-gray-500">
            <div className="text-gray-400 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No violations found</h3>
            <p className="text-gray-500">
              {filter !== 'all' || typeFilter !== 'all'
                ? 'No violations match the current filters.'
                : 'No content moderation violations have been detected yet.'
              }
            </p>
          </div>
        )}
      </div>

      {/* Violation Summary */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Violation Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-700">By Severity</h4>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span>High Risk:</span>
                <span className="font-medium text-red-600">
                  {violations.filter(v => v.severity === 'high').length}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Medium Risk:</span>
                <span className="font-medium text-yellow-600">
                  {violations.filter(v => v.severity === 'medium').length}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Low Risk:</span>
                <span className="font-medium text-blue-600">
                  {violations.filter(v => v.severity === 'low').length}
                </span>
              </div>
            </div>
          </div>
          
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-700">By Type</h4>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span>Blocked:</span>
                <span className="font-medium text-red-600">
                  {violations.filter(v => v.violation_type === 'blocked').length}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Warning:</span>
                <span className="font-medium text-orange-600">
                  {violations.filter(v => v.violation_type === 'warning').length}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Suspicious:</span>
                <span className="font-medium text-purple-600">
                  {violations.filter(v => v.violation_type === 'suspicious').length}
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-700">Top Violations</h4>
            <div className="space-y-1 text-sm">
              {Object.entries(
                violations.reduce((acc, v) => {
                  acc[v.violation_reason] = (acc[v.violation_reason] || 0) + 1
                  return acc
                }, {} as Record<string, number>)
              )
                .sort(([,a], [,b]) => b - a)
                .slice(0, 3)
                .map(([reason, count]) => (
                  <div key={reason} className="flex justify-between">
                    <span className="truncate">{reason}:</span>
                    <span className="font-medium">{count}</span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>

      {/* Help Section */}
      <div className="bg-blue-50 rounded-lg p-6">
        <h3 className="text-lg font-medium text-blue-900 mb-3">🛡️ Content Moderation System</h3>
        <div className="text-sm text-blue-800 space-y-2">
          <p><strong>Purpose:</strong> Prevent users from sharing contact information or moving conversations off-platform</p>
          <p><strong>Blocked Content:</strong> Account numbers, phone numbers, WhatsApp contacts, social media handles</p>
          <p><strong>Action Taken:</strong> Messages are blocked, users receive warnings, and violations are logged for admin review</p>
          <p><strong>Admin Benefits:</strong> Full visibility into user behavior, early fraud detection, platform integrity maintenance</p>
        </div>
      </div>
    </div>
  )
}


